// useProvenance — turns a context-graph (DF-4) into the resolved provenance the citation primitives
// render: the retrieved+cited CHUNKS (with content, from GET /context-graphs/{id}/chunks) joined to
// their SOURCE artifacts (DF-2 /sources), numbered cited-first. All reads go through useScopeData so
// the ethical wall holds (the endpoint is wall-enforced on the graph's scope; /sources on the reader's).
import { useCallback } from "react";

import { useScopeData } from "../state/useScopeData";
import { buildCitationId } from "./types";
import type { CitationRef, LineageBinding, ProvenanceChunk, SourceLite } from "./types";

/** The fields of a /context-graphs row the Lineage view needs (from ConnectionsModule's existing fetch). */
export interface ContextGraphLite {
  id: string;
  label?: string | null;
  kind: string;
  verdict?: string | null;
  confidence?: number | null;
  retrieved_chunk_ids: string[];
  cited_chunk_ids: string[];
}

export interface Lineage {
  binding: LineageBinding;
  citations: CitationRef[];
}

export interface ProvenanceApi {
  ready: boolean;
  /** Fetch + join a context-graph's chunks to their sources → {binding, citations}. Throws on a walled failure. */
  loadLineage: (cg: ContextGraphLite) => Promise<Lineage>;
}

export function useProvenance(): ProvenanceApi {
  const { ready, fetchScoped } = useScopeData();

  const loadLineage = useCallback(
    async (cg: ContextGraphLite): Promise<Lineage> => {
      const [chunks, sourcesPage] = await Promise.all([
        fetchScoped<ProvenanceChunk[]>(`/context-graphs/${cg.id}/chunks`),
        // D-10: /sources returns the canonical Page envelope — read .items.
        fetchScoped<{ items: SourceLite[] }>("/sources"),
      ]);
      const sourceById = new Map(sourcesPage.items.map((s) => [s.id, s]));
      // Number cited chunks first (1..M), then retrieved-but-not-cited — gives stable [n] markers.
      const cited = chunks.filter((c) => c.role === "cited");
      const retrievedOnly = chunks.filter((c) => c.role !== "cited");
      // Row 19.5 — citationId/sourceType/sourceRef are computed here (no backend column, no
      // migration): the context-graph + chunk pair already uniquely identifies a citation.
      // claimSpan/passageSpan are left undefined — no live producer populates them yet (CFR);
      // the fields exist so a real value slots in later without a breaking change.
      const citations: CitationRef[] = [...cited, ...retrievedOnly].map((chunk, i) => ({
        index: i + 1,
        chunk,
        source: sourceById.get(chunk.source_id),
        citationId: buildCitationId(cg.id, chunk.chunk_id),
        sourceType: "source_artifact",
        sourceRef: { sourceId: chunk.source_id, chunkId: chunk.chunk_id },
      }));
      const binding: LineageBinding = {
        id: cg.id,
        label: cg.label,
        kind: cg.kind,
        verdict: cg.verdict,
        confidence: cg.confidence,
        retrievedCount: cg.retrieved_chunk_ids.length,
        citedCount: cg.cited_chunk_ids.length,
      };
      return { binding, citations };
    },
    [fetchScoped],
  );

  return { ready, loadLineage };
}
